import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Warning, Buildings, Phone, EnvelopeSimple, ArrowsClockwise, Check } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

export function DuplicatesPanel({ onNavigate }) {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null);
  const [selectedMerge, setSelectedMerge] = useState({});

  const fetchDuplicates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comercial/duplicates/detect`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      process.env.NODE_ENV === "development" && console.error(e);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchDuplicates(); }, [fetchDuplicates]);

  const handleMerge = async (companyGroup) => {
    const selected = selectedMerge[companyGroup.company] || {};
    const primaryId = selected.primary;
    const mergeIds = companyGroup.ids.filter(id => id !== primaryId);
    if (!primaryId || mergeIds.length === 0) {
      toast.error("Selecciona cual contacto conservar como principal");
      return;
    }
    setMerging(companyGroup.company);
    try {
      await axios.post(`${API}/comercial/merge`, { primary_id: primaryId, merge_ids: mergeIds }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Se unificaron ${mergeIds.length} registro(s) en "${companyGroup.company}"`);
      fetchDuplicates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al unificar");
    } finally { setMerging(null); }
  };

  const selectPrimary = (company, id) => {
    setSelectedMerge(prev => ({ ...prev, [company]: { ...prev[company], primary: id } }));
  };

  const canMerge = user?.role === "admin" || user?.role === "subgerente";
  const summary = data?.summary || {};
  const totalAlerts = (summary.duplicate_companies || 0) + (summary.duplicate_phones || 0) + (summary.duplicate_emails || 0);

  if (loading) return <div className="text-xs text-slate-400 p-4 text-center">Analizando duplicados...</div>;
  if (!data || totalAlerts === 0) return (
    <div className="text-center py-6 px-4">
      <Check size={28} className="mx-auto text-emerald-500 mb-2" />
      <p className="text-sm font-semibold text-slate-700">Sin duplicados</p>
      <p className="text-xs text-slate-400 mt-0.5">No se encontraron contactos o empresas duplicadas</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="duplicates-panel">
      {/* Summary badge */}
      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
        <Warning size={16} className="text-amber-600 shrink-0" />
        <p className="text-xs text-amber-800">
          <b>{totalAlerts}</b> alerta(s): {summary.duplicate_companies || 0} empresas, {summary.duplicate_phones || 0} telefonos, {summary.duplicate_emails || 0} emails duplicados
        </p>
      </div>

      {/* Duplicate Companies */}
      {(data.duplicate_companies || []).map((group, idx) => (
        <div key={`dup-co-${idx}`} className="bg-white border border-amber-200 rounded-xl overflow-hidden" data-testid={`dup-company-${idx}`}>
          <div className="px-3 py-2.5 bg-amber-50/50 flex items-center gap-2 border-b border-amber-100">
            <Buildings size={14} className="text-amber-600" />
            <span className="text-xs font-bold text-amber-800 flex-1 truncate">{group.company}</span>
            <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">{group.count}x</span>
          </div>
          <div className="p-2.5 space-y-1.5">
            {group.ids.map((id, i) => (
              <div key={id} className={`flex items-center gap-2 p-2 rounded-lg border text-xs cursor-pointer transition-all ${selectedMerge[group.company]?.primary === id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                onClick={() => selectPrimary(group.company, id)}>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${selectedMerge[group.company]?.primary === id ? "border-blue-500 bg-blue-500" : "border-slate-300"}`}>
                  {selectedMerge[group.company]?.primary === id && <Check size={10} className="text-white" />}
                </div>
                <span className="text-slate-700 truncate flex-1">{group.contacts[i] || "Sin contacto"}</span>
              </div>
            ))}
            {canMerge && group.count > 1 && (
              <Button size="sm" variant="outline" className="w-full mt-1.5 h-8 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
                disabled={!selectedMerge[group.company]?.primary || merging === group.company}
                onClick={() => handleMerge(group)} data-testid={`merge-btn-${idx}`}>
                <ArrowsClockwise size={12} className="mr-1" />
                {merging === group.company ? "Unificando..." : "Unificar (conservar seleccionado)"}
              </Button>
            )}
          </div>
        </div>
      ))}

      {/* Duplicate Phones */}
      {(data.duplicate_phones || []).slice(0, 8).map((group, idx) => (
        <div key={`dup-ph-${idx}`} className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <Phone size={13} className="text-slate-500" />
            <span className="text-xs font-bold text-slate-700">{group.phone}</span>
            <span className="text-[10px] bg-slate-200 px-1.5 py-0.5 rounded-full font-bold">{group.count}x</span>
          </div>
          {group.entries.map((e, i) => (
            <p key={`${e.id}-${i}`} className="text-[10px] text-slate-500 pl-5 truncate">{e.company_name} — {e.contact_name}</p>
          ))}
        </div>
      ))}

      {/* Duplicate Emails */}
      {(data.duplicate_emails || []).slice(0, 5).map((group, idx) => (
        <div key={`dup-em-${idx}`} className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <EnvelopeSimple size={13} className="text-slate-500" />
            <span className="text-xs font-bold text-slate-700 truncate">{group.email}</span>
            <span className="text-[10px] bg-slate-200 px-1.5 py-0.5 rounded-full font-bold">{group.count}x</span>
          </div>
          {group.entries.map((e, i) => (
            <p key={`${e.id}-${i}`} className="text-[10px] text-slate-500 pl-5 truncate">{e.company_name} — {e.contact_name}</p>
          ))}
        </div>
      ))}

      {/* Refresh */}
      <button onClick={() => { setLoading(true); fetchDuplicates(); }} className="w-full text-center text-[10px] text-slate-400 hover:text-blue-500 py-2" data-testid="refresh-duplicates">
        <ArrowsClockwise size={12} className="inline mr-1" /> Actualizar analisis
      </button>
    </div>
  );
}
